export function applyOneTrustPreferenceCenterA11yEnhancements() {
  setPreferenceCenterDialogId();
  enhancePreferenceCenterContent();
  enhancePreferenceCenterToggle();
  enhancePreferenceCenterAccordionToggles();
  enhancePreferenceCenterInteractiveControls();
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function setPreferenceCenterDialogId() {
  const dialog = oneTrustElements.getPreferenceCenterDialog();

  if (!dialog) return;

  dialog.id = ONE_TRUST_PC_DIALOG_ID;

  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('tabindex', '-1');

  const title = oneTrustElements.getPreferenceCenterTitle();

  if (title) {
    title.setAttribute('tabindex', '-1');
    dialog.removeAttribute('aria-label');
    dialog.setAttribute('aria-labelledby', title.id);

    return;
  }

  if (dialog.hasAttribute('aria-label') || dialog.hasAttribute('aria-labelledby')) return;

  dialog.setAttribute('aria-label', 'Your Privacy Choices');
}

function enhancePreferenceCenterContent() {
  const content = oneTrustElements.getPreferenceCenterContent();

  if (!content) return;

  content.setAttribute('role', 'document');
  content.removeAttribute('tabindex');
}

function enhancePreferenceCenterToggle() {
  const toggle = oneTrustElements.getPreferenceCenterToggle();

  if (!toggle) return;

  toggle.removeAttribute('aria-label');
  toggle.setAttribute('aria-haspopup', 'dialog');
  toggle.setAttribute('aria-controls', ONE_TRUST_PC_DIALOG_ID);

  addPreferenceCenterToggleListeners(toggle);
}

function addPreferenceCenterToggleListeners(toggle: Element) {
  if (toggle.hasAttribute(PREFERENCE_TOGGLE_LISTENERS_APPLIED_ATTRIBUTE)) return;

  toggle.setAttribute(PREFERENCE_TOGGLE_LISTENERS_APPLIED_ATTRIBUTE, 'true');
  toggle.addEventListener('click', handlePreferenceCenterToggleOpen);
  toggle.addEventListener('keydown', handlePreferenceCenterToggleKeyDown);
}

function handlePreferenceCenterToggleOpen() {
  schedulePreferenceCenterSemanticEnhancements();
}

function handlePreferenceCenterToggleKeyDown(event: Event) {
  if (!(event instanceof KeyboardEvent)) return;

  if (!checkIfActivationKey(event)) return;

  schedulePreferenceCenterSemanticEnhancements();
}

function schedulePreferenceCenterSemanticEnhancements() {
  requestAnimationFrame(() => {
    applyOneTrustPreferenceCenterA11yEnhancements();

    requestAnimationFrame(() => {
      applyOneTrustPreferenceCenterA11yEnhancements();
      focusPreferenceCenterInitialContent();
    });
  });
}

function focusPreferenceCenterInitialContent() {
  const preferenceCenter = oneTrustElements.getPreferenceCenter();
  const focusTarget = oneTrustElements.getPreferenceCenterInitialFocusTarget();
  const activeElement = document.activeElement;

  if (!preferenceCenter || !focusTarget) return;

  if (checkIfPreferenceCenterHidden(preferenceCenter)) return;

  if (checkIfFocusShouldStayInPlace({ activeElement, preferenceCenter })) return;

  focusTarget.focus();
}

function checkIfActivationKey(event: KeyboardEvent) {
  return event.key === 'Enter' || event.key === ' ';
}

function checkIfPreferenceCenterHidden(preferenceCenter: Element) {
  return (
    preferenceCenter.classList.contains('ot-hide') ||
    preferenceCenter.hasAttribute('hidden') ||
    preferenceCenter.getAttribute('aria-hidden') === 'true'
  );
}

function checkIfFocusShouldStayInPlace(params: CheckIfFocusShouldStayInPlaceParams) {
  const { activeElement, preferenceCenter } = params;

  if (!(activeElement instanceof HTMLElement)) return false;

  if (!preferenceCenter.contains(activeElement)) return false;

  return activeElement.id !== PREFERENCE_CENTER_CLOSE_BUTTON_ID;
}

function enhancePreferenceCenterAccordionToggles() {
  const toggles = oneTrustElements.getPreferenceCenterAccordionToggles();

  toggles.forEach(enhancePreferenceCenterAccordionToggle);
}

function enhancePreferenceCenterAccordionToggle(toggle: HTMLElement) {
  toggle.setAttribute('tabindex', '0');
}

function enhancePreferenceCenterInteractiveControls() {
  const controls = oneTrustElements.getPreferenceCenterLabelledControls();

  controls.forEach(enhancePreferenceCenterInteractiveControl);
}

function enhancePreferenceCenterInteractiveControl(control: HTMLElement) {
  const label = getAccessibleLabel(control);

  if (!label) return;

  control.setAttribute('aria-label', label);
  control.removeAttribute('aria-labelledby');
}

function getAccessibleLabel(element: HTMLElement) {
  const existingLabel = getNormalizedText(element.getAttribute('aria-label'));

  if (existingLabel) return existingLabel;

  const labelledBy = element.getAttribute('aria-labelledby');

  if (!labelledBy) return getNormalizedText(element.textContent);

  const label = labelledBy.split(/\s+/).map(getElementTextById).filter(Boolean).join(' ');

  return getNormalizedText(label);
}

function getElementTextById(id: string) {
  const element = document.getElementById(id);

  return getNormalizedText(element?.textContent);
}

function getNormalizedText(text: string | null | undefined) {
  return text?.trim().replace(/\s+/g, ' ');
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ONE_TRUST_PC_DIALOG_ID = 'onetrust-pc-dialog';
const PREFERENCE_CENTER_CLOSE_BUTTON_ID = 'close-pc-btn-handler';
const PREFERENCE_TOGGLE_LISTENERS_APPLIED_ATTRIBUTE = 'data-a11y-toggle-listeners-applied';

const oneTrustElements = {
  getPreferenceCenter: () => document.querySelector('#onetrust-pc-sdk'),
  getPreferenceCenterInitialFocusTarget: () =>
    document.querySelector<HTMLElement>('#ot-pc-title') ??
    document.querySelector<HTMLElement>('#onetrust-pc-sdk [role="dialog"]'),
  getPreferenceCenterToggle: () => document.querySelector('#onetrust-pc-btn-handler'),
  getPreferenceCenterDialog: () => document.querySelector('#onetrust-pc-sdk [role="dialog"]'),
  getPreferenceCenterTitle: () => document.querySelector<HTMLElement>('#ot-pc-title'),
  getPreferenceCenterContent: () => document.querySelector('#ot-pc-content'),
  getPreferenceCenterAccordionToggles: () =>
    document.querySelectorAll<HTMLElement>('#onetrust-pc-sdk button[ot-accordion="true"]'),
  getPreferenceCenterLabelledControls: () =>
    document.querySelectorAll<HTMLElement>(
      '#onetrust-pc-sdk button[aria-labelledby], #onetrust-pc-sdk input[aria-labelledby]',
    ),
};

// ─── Types ───────────────────────────────────────────────────────────────────

type CheckIfFocusShouldStayInPlaceParams = {
  activeElement: Element | null;
  preferenceCenter: Element;
};
